import classNames from 'classnames';
import React, {
    FC,
    useState,
    useEffect,
    useCallback,
    useRef,
    useLayoutEffect,
    useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { TType } from '../../common/types';
import { hideCode } from '../../store/form';
import { appCodeSelector, languageSelector } from '../../store/form/selectors';
import { useAppSelector } from '../../store/hooks';
import styles from './Menu.module.scss';
import getMenuEnties, { TMenuEntry } from './data';

interface MenuItemProps extends TMenuEntry {
    onChange: (id: TType) => void;
    isActive: boolean;
    underlineRef: React.RefObject<HTMLDivElement>;
}

const MenuItem: FC<MenuItemProps> = ({
    id,
    title,
    imageUrl,
    onChange,
    isActive,
    underlineRef,
}): JSX.Element => {
    const itemRef = useRef<HTMLButtonElement>(null);
    useLayoutEffect(() => {
        if (!isActive) return;

        // Переместить выделение
        const underline = underlineRef?.current;
        const self = itemRef?.current;
        if (!underline || !self) return;
        underline.style.left = `${self.offsetLeft}px`;
        underline.style.width = `${self.offsetWidth}px`;

        // Проскроллить меню
        const parent = self.parentElement?.parentElement;
        if (!parent || !parent.scroll) return;
        const offset = self.offsetLeft;
        parent.scroll({ left: offset, behavior: 'smooth' });
    }, [isActive, underlineRef]);
    useAppSelector(languageSelector);

    return (
        <button
            className={classNames(styles['menu-item'], { [styles['item-active']]: isActive })}
            onClick={() => onChange(id)}
            type="button"
            ref={itemRef}
        >
            <span
                style={{ backgroundImage: `url(${imageUrl})` }}
                className={styles['item-image']}
            />
            <span className={styles['item-title']}>{title}</span>
        </button>
    );
};

export interface MenuProps {
    active?: string;
    setActive?: (title: string) => void;
}

const Menu: FC<MenuProps> = ({
    active: defaultActive = getMenuEnties()[0].title,
    setActive: defaultSetActive,
}): JSX.Element => {
    const [active, setActive] = useState('');
    const underlineRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const lang = useAppSelector(languageSelector);
    const appCode = useAppSelector(appCodeSelector);
    useEffect(() => setActive(defaultActive), [defaultActive]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const menuItems = useMemo(() => getMenuEnties(appCode), [lang, appCode]);
    const changeActiveItem = useCallback(
        (id: TType) => {
            setActive(id);
            dispatch(hideCode());
            defaultSetActive?.(id);
        },
        [defaultSetActive, dispatch]
    );
    return (
        <div className={classNames(styles['menu-container'], 'dragscroll')}>
            <div className={styles['items-container']}>
                {menuItems.map(({ id, title, imageUrl }) => (
                    <MenuItem
                        key={id}
                        title={title}
                        imageUrl={imageUrl}
                        onChange={changeActiveItem}
                        isActive={active === id}
                        underlineRef={underlineRef}
                        id={id}
                    />
                ))}
            </div>
            <div className={styles['menu-underline']} ref={underlineRef} />
        </div>
    );
};

export default Menu;
